import _ from 'lodash';
import { Reducer } from 'react';
import Constants from '../constants';
import ContentConstants from '../contentConstants';
import { EditState, IBenchmark, IHierarchyMetadata, IReportingSetupModel, IReportingUser, IValidationModel, SurveyStep } from '../models';
import { IBreakoutModel, IBreakoutModelVm } from '../models/IBreakoutModel';
import { IHierarchyReportingAccessConfig } from '../models/IHierarchyReportingAccessConfig';
import { ITrendGroup } from '../models/ITrendGroup';
import { ITrendSurvey } from '../models/ITrendSurvey';
import * as ReportingSetupAPI from '../services/ReportingSetupAPI';
import * as SurveyTrendGroupAPI from '../services/SurveyTrendGroupAPI';
import * as TrendGroupAPI from '../services/TrendGroupAPI';
import { ReportingHelper } from '../utilities';
import { IApplicationState } from './';
import {
	IHierarchyReportingAccessConfigsAddSucceeded,
	IHierarchyReportingAccessConfigsGetRequested,
	IHierarchyReportingAccessConfigsGetSucceeded,
	IHierarchyReportingAccessConfigsUpdateSucceeded,
	IParticipationMonitorAttributesUpdateSelected,
	IParticipationMonitorHierarchiesUpdateSelected,
	IParticipationMonitorMissingBreakoutsUpdated,
	IReportingClientMinNGetFailed,
	IReportingClientMinNGetRequested,
	IReportingClientMinNGetSucceeded,
	IReportingSetupChangeTrendGroup,
	IReportingSetupGetFailed,
	IReportingSetupGetRequested,
	IReportingSetupGetSuccessful,
	IReportingSetupMinNUpdated,
	IReportingSetupResponseStreamingFlagChanged,
	IReportingSetupSaveFailed,
	IReportingSetupSaveRequested,
	IReportingSetupSaveSuccessful,
	IReportingSetupTrendGroupsGetFailed,
	IReportingSetupTrendGroupsGetRequested,
	IReportingSetupTrendGroupsGetSuccessful,
	IReportingSetupTrendSurveysGetFailed,
	IReportingSetupTrendSurveysGetRequested,
	IReportingSetupTrendSurveysGetSuccessful,
	IReportingSetupUnmount,
	IReportingUsersAddUser,
	IReportingUsersDeleteUser,
	IReportingUsersUpdateUser,
	IStepperRoutingDiscardChanges,
} from './Actions';
import { ActionTypes } from './ActionTypes';

export interface IReportingSetupState {
	loading: boolean;
	reportingSetupModel: IReportingSetupModel;
	trendSurveys: ITrendSurvey[];
	trendSurveysLoading: boolean;
	trendGroups: ITrendGroup[];
	selectedTrendGroupName: string;
	firstTrendGroupItem: ITrendGroup;
	lastSavedReportingSetupModel: IReportingSetupModel;
	benchmarks: IBenchmark[];
	missingBreakouts: IBreakoutModel;
}

const firstTrendGroupItem: ITrendGroup = {
	trendGroupId: Constants.EMPTY_GUID,
	name: 'NoMatch',
	displayText: "I can't find a match",
};

export const defaultReportingModel: IReportingSetupModel = {
	surveyId: 0,
	isResponseStreamed: false,
	currentStep: SurveyStep.Reporting,
	reportingUsers: [],
	selectedTrendGroupId: undefined,
	participationMonitorBreakout: { attributes: [], hierarchies: [] },
	audienceId: 0,
};

export const defaultState: IReportingSetupState = {
	loading: false,
	reportingSetupModel: { ...defaultReportingModel },
	lastSavedReportingSetupModel: { ...defaultReportingModel },
	benchmarks: [],
	trendGroups: [],
	trendSurveys: [],
	trendSurveysLoading: false,
	firstTrendGroupItem,
	selectedTrendGroupName: ContentConstants.REPORTING_TRENDS_PREVIOUS_SURVEYS_DEFAULT_SELECTION,
	missingBreakouts: { attributes: [], hierarchies: [] },
};

export const ActionCreators = {
	AddUser: (surveyId: number) => (dispatch: any, getState: () => IApplicationState) => {
		const { reportingSetupModel } = getState().reportingSetup;
		const newUser = ReportingHelper.initializeNewUser(surveyId, reportingSetupModel.reportingUsers.length);
		dispatch({ type: ActionTypes.REPORTING_USERS_ADD_USER, payload: newUser });
	},
	CheckForMissingBreakouts: (availableBreakouts: IBreakoutModelVm, selectedBreakouts: IBreakoutModel) => (dispatch: any) => {
		const missingBreakouts: IBreakoutModel = { attributes: [], hierarchies: [] };

		missingBreakouts.attributes = _.filter(selectedBreakouts.attributes, (attribute: string) => {
			const availableAttribute = _.find(
				availableBreakouts.attributes,
				(a: string) => a.localeCompare(attribute, undefined, { sensitivity: 'accent' }) === 0,
			);
			return availableAttribute === undefined;
		});
		_.pullAll(selectedBreakouts.attributes, missingBreakouts.attributes);

		missingBreakouts.hierarchies = _.filter(selectedBreakouts.hierarchies, (hierarchy: string) => {
			const availableHierarchy = _.find(availableBreakouts.hierarchies, (h: IHierarchyMetadata) => h.hierarchyId === hierarchy);
			return availableHierarchy === undefined;
		});
		_.pullAll(selectedBreakouts.hierarchies, missingBreakouts.hierarchies);

		dispatch({ type: ActionTypes.PARTICIPATION_MONITOR_MISSING_BREAKOUTS_UPDATED, payload: { missingBreakouts, selectedBreakouts } });
	},
	ChangeTrendGroup: (surveyId: number, trendGroupId: string) => (dispatch: any) => {
		dispatch({ type: ActionTypes.REPORTING_SETUP_UPDATE_TREND_GROUP, payload: trendGroupId });

		if (trendGroupId === Constants.EMPTY_GUID) {
			return dispatch({ type: ActionTypes.REPORTING_SETUP_TRENDSURVEYS_GET_SUCCEEDED, payload: [] });
		} else {
			dispatch({ type: ActionTypes.REPORTING_SETUP_TRENDSURVEYS_GET_REQUESTED });

			return SurveyTrendGroupAPI.get(surveyId, trendGroupId)
				.then((trendSurveys: ITrendSurvey[]) => {
					dispatch({ type: ActionTypes.REPORTING_SETUP_TRENDSURVEYS_GET_SUCCEEDED, payload: trendSurveys });
				})
				.catch(() => dispatch({ type: ActionTypes.REPORTING_SETUP_TRENDSURVEYS_GET_FAILED }));
		}
	},
	DeleteUser: (user: IReportingUser) => ({ type: ActionTypes.REPORTING_USERS_DELETE_USER, payload: user }),
	GetReportingSetupModel: (surveyId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.REPORTING_SETUP_GET_REQUESTED });

		return ReportingSetupAPI.get(surveyId)
			.then((reportingSetupModel: IValidationModel<IReportingSetupModel>) =>
				dispatch({ type: ActionTypes.REPORTING_SETUP_GET_SUCCEEDED, payload: reportingSetupModel.subject }),
			)
			.catch(() => dispatch({ type: ActionTypes.REPORTING_SETUP_GET_FAILED }));
	},
	GetTrendGroups: (surveyId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.REPORTING_SETUP_TRENDGROUPS_GET_REQUESTED });

		return TrendGroupAPI.get(surveyId)
			.then((trendGroups: ITrendGroup[]) => {
				dispatch({ type: ActionTypes.REPORTING_SETUP_TRENDGROUPS_GET_SUCCEEDED, payload: trendGroups });
			})
			.catch(() => dispatch({ type: ActionTypes.REPORTING_SETUP_TRENDGROUPS_GET_FAILED }));
	},
	GetTrendSurveys: (surveyId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.REPORTING_SETUP_TRENDSURVEYS_GET_REQUESTED });

		return SurveyTrendGroupAPI.getTrendSurveys(surveyId)
			.then((trendSurveys: ITrendSurvey[]) => {
				dispatch({ type: ActionTypes.REPORTING_SETUP_TRENDSURVEYS_GET_SUCCEEDED, payload: trendSurveys });
			})
			.catch(() => dispatch({ type: ActionTypes.REPORTING_SETUP_TRENDSURVEYS_GET_FAILED }));
	},
	MinNChange: (e: any) => (dispatch: any) => {
		dispatch({ type: ActionTypes.REPORTING_MIN_N_UPDATED, payload: e.target.value });
	},
	ResponseStreamFlagChanged: () => (dispatch: any) => {
		dispatch({ type: ActionTypes.REPORTING_RESPONSE_STREAMING_FLAG_CHANGED });
	},
	UnmountReportingSetup: () => ({ type: ActionTypes.REPORTING_SETUP_UNMOUNT }),
	UpdateHierarchyReportingAccessConfig: (surveyId: number, model: IHierarchyReportingAccessConfig) => ({
		type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_UPDATE_SUCCEEDED,
		payload: model,
	}),
	UpdateSelectedAttributes: (attributes: string[]) => (dispatch: any) => {
		dispatch({ type: ActionTypes.PARTICIPATION_MONITOR_ATTRIBUTES_UPDATE_SELECTED, payload: attributes });
	},
	UpdateSelectedHierarchies: (hierarchyAttributes: string[]) => (dispatch: any) => {
		dispatch({ type: ActionTypes.PARTICIPATION_MONITOR_HIERARCHIES_UPDATE_SELECTED, payload: hierarchyAttributes });
	},
	UpdateUser: (user: IReportingUser) => ({ type: ActionTypes.REPORTING_USERS_UPDATE_USER, payload: user }),
};

export type KnownActions =
	| IHierarchyReportingAccessConfigsAddSucceeded
	| IHierarchyReportingAccessConfigsGetRequested
	| IHierarchyReportingAccessConfigsGetSucceeded
	| IHierarchyReportingAccessConfigsUpdateSucceeded
	| IParticipationMonitorAttributesUpdateSelected
	| IParticipationMonitorHierarchiesUpdateSelected
	| IParticipationMonitorMissingBreakoutsUpdated
	| IReportingClientMinNGetFailed
	| IReportingClientMinNGetRequested
	| IReportingClientMinNGetSucceeded
	| IReportingSetupChangeTrendGroup
	| IReportingSetupGetFailed
	| IReportingSetupGetRequested
	| IReportingSetupGetSuccessful
	| IReportingSetupMinNUpdated
	| IReportingSetupSaveFailed
	| IReportingSetupSaveRequested
	| IReportingSetupSaveSuccessful
	| IReportingSetupTrendGroupsGetFailed
	| IReportingSetupTrendGroupsGetRequested
	| IReportingSetupTrendGroupsGetSuccessful
	| IReportingSetupTrendSurveysGetFailed
	| IReportingSetupTrendSurveysGetRequested
	| IReportingSetupTrendSurveysGetSuccessful
	| IReportingUsersAddUser
	| IReportingUsersDeleteUser
	| IReportingUsersUpdateUser
	| IStepperRoutingDiscardChanges
	| IReportingSetupResponseStreamingFlagChanged
	| IReportingSetupUnmount;

export const reducer: Reducer<IReportingSetupState, KnownActions> = (
	state: IReportingSetupState | undefined,
	action: KnownActions,
): IReportingSetupState => {
	if (!state) {
		state = { ...defaultState };
	}
	switch (action.type) {
		case ActionTypes.PARTICIPATION_MONITOR_ATTRIBUTES_UPDATE_SELECTED: {
			const partMonAttributes: IBreakoutModel = {
				attributes: action.payload,
				hierarchies: state.reportingSetupModel.participationMonitorBreakout.hierarchies,
			};
			return { ...state, reportingSetupModel: { ...state.reportingSetupModel, participationMonitorBreakout: partMonAttributes } };
		}
		case ActionTypes.PARTICIPATION_MONITOR_HIERARCHIES_UPDATE_SELECTED: {
			const partMonAttributes: IBreakoutModel = {
				hierarchies: action.payload,
				attributes: state.reportingSetupModel.participationMonitorBreakout.attributes,
			};
			return { ...state, reportingSetupModel: { ...state.reportingSetupModel, participationMonitorBreakout: partMonAttributes } };
		}
		case ActionTypes.PARTICIPATION_MONITOR_MISSING_BREAKOUTS_UPDATED: {
			return {
				...state,
				reportingSetupModel: { ...state.reportingSetupModel, participationMonitorBreakout: action.payload.selectedBreakouts },
				missingBreakouts: action.payload.missingBreakouts,
			};
		}
		case ActionTypes.REPORTING_SETUP_GET_FAILED:
		case ActionTypes.REPORTING_SETUP_TRENDGROUPS_GET_FAILED:
		case ActionTypes.REPORTING_SETUP_TRENDSURVEYS_GET_FAILED:
			return { ...state, loading: false, trendSurveysLoading: false };
		case ActionTypes.REPORTING_SETUP_GET_REQUESTED:
		case ActionTypes.REPORTING_SETUP_TRENDGROUPS_GET_REQUESTED:
			return { ...state, loading: true };
		case ActionTypes.REPORTING_SETUP_GET_SUCCEEDED:
		case ActionTypes.REPORTING_SETUP_SAVE_SUCCEEDED: {
			const reportingUsers = action.payload.reportingUsers.map((user: IReportingUser, id: number) => ({ ...user, id }));
			return {
				...state,
				loading: false,
				reportingSetupModel: {
					...action.payload,
					reportingUsers,
				},
				lastSavedReportingSetupModel: {
					...action.payload,
					reportingUsers,
				},
				missingBreakouts: { attributes: [], hierarchies: [] },
			};
		}
		case ActionTypes.REPORTING_SETUP_TRENDGROUPS_GET_SUCCEEDED: {
			const trendGroupId = state.reportingSetupModel.selectedTrendGroupId;
			const trendGroup = _.find(action.payload, (tg: ITrendGroup) => tg.trendGroupId === trendGroupId);
			return {
				...state,
				loading: false,
				trendGroups: action.payload,
				selectedTrendGroupName: trendGroup ? trendGroup.displayText : ContentConstants.REPORTING_TRENDS_PREVIOUS_SURVEYS_DEFAULT_SELECTION,
			};
		}
		case ActionTypes.REPORTING_SETUP_TRENDSURVEYS_GET_REQUESTED:
			return { ...state, trendSurveysLoading: true };
		case ActionTypes.REPORTING_SETUP_TRENDSURVEYS_GET_SUCCEEDED: {
			return {
				...state,
				trendSurveysLoading: false,
				trendSurveys: action.payload,
			};
		}
		case ActionTypes.REPORTING_SETUP_UNMOUNT:
			return { ...defaultState };
		case ActionTypes.REPORTING_CLIENT_MIN_N_GET_FAILED:
		case ActionTypes.REPORTING_CLIENT_MIN_N_GET_REQUESTED: {
			return { ...state };
		}
		case ActionTypes.REPORTING_CLIENT_MIN_N_GET_SUCCEEDED:
		case ActionTypes.REPORTING_MIN_N_UPDATED: {
			return {
				...state,
				reportingSetupModel: {
					...state.reportingSetupModel,
					surveyMinN: action.payload,
				},
			};
		}
		case ActionTypes.REPORTING_RESPONSE_STREAMING_FLAG_CHANGED: {
			return {
				...state,
				reportingSetupModel: {
					...state.reportingSetupModel,
					isResponseStreamed: !state.reportingSetupModel.isResponseStreamed,
				},
			};
		}
		case ActionTypes.REPORTING_USERS_ADD_USER: {
			const reportingUsers: IReportingUser[] = [...state.reportingSetupModel.reportingUsers, action.payload];
			return {
				...state,
				reportingSetupModel: {
					...state.reportingSetupModel,
					reportingUsers,
				},
			};
		}
		case ActionTypes.REPORTING_USERS_DELETE_USER: {
			const reportingUsers: IReportingUser[] = [...state.reportingSetupModel.reportingUsers];
			const index = _.findIndex(reportingUsers, (user: IReportingUser) => user.id === action.payload.id);
			reportingUsers[index] = { ...action.payload, editState: EditState.Remove };

			return {
				...state,
				reportingSetupModel: {
					...state.reportingSetupModel,
					reportingUsers,
				},
			};
		}
		case ActionTypes.REPORTING_USERS_UPDATE_USER: {
			const editState = action.payload.editState === EditState.Add ? action.payload.editState : EditState.Update;

			const reportingUsers: IReportingUser[] = [...state.reportingSetupModel.reportingUsers];
			const index = _.findIndex(reportingUsers, (user: IReportingUser) => user.id === action.payload.id);
			reportingUsers[index] = { ...action.payload, editState };

			return {
				...state,
				reportingSetupModel: {
					...state.reportingSetupModel,
					reportingUsers,
				},
			};
		}
		case ActionTypes.REPORTING_SETUP_UPDATE_TREND_GROUP: {
			const trendGroup = _.find(state.trendGroups, (tg: ITrendGroup) => tg.trendGroupId === action.payload);
			return {
				...state,
				reportingSetupModel: { ...state.reportingSetupModel, selectedTrendGroupId: action.payload },
				selectedTrendGroupName: trendGroup ? trendGroup.displayText : ContentConstants.REPORTING_TRENDS_PREVIOUS_SURVEYS_DEFAULT_SELECTION,
			};
		}
		case ActionTypes.STEPPER_ROUTING_DISCARD_CHANGES:
			return {
				...state,
				reportingSetupModel: {
					...state.lastSavedReportingSetupModel,
				},
			};
	}
	return state;
};
